import { Toast, ToastContent, type ToastMessage } from '@ics-portal/guardian';
import {
    createContext,
    type Dispatch,
    type PropsWithChildren,
    type ReactNode,
    type SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { Navigation } from '@models/Navigation';
import { useMenuContext } from './MenuContext';

interface ToastContext {
    pushToast: (toastMessage: ToastMessage) => void;
    pushSuccessToast: (toastMessage: Omit<ToastMessage, 'severity'>) => void;
    pushErrorToast: (toastMessage: Omit<ToastMessage, 'severity'>) => void;
    pushWarningToast: (toastMessage: Omit<ToastMessage, 'severity'>) => void;
    pushInfoToast: (toastMessage: Omit<ToastMessage, 'severity'>) => void;
    dismissToast: (id: string) => void;
}

interface Values extends ToastContext {
    appClassName: 'collapsed' | 'expanded';
    setAppClassName: Dispatch<SetStateAction<'collapsed' | 'expanded'>>;
    appSidebarHidden: boolean;
    setAppSidebarHidden: Dispatch<SetStateAction<boolean>>;
    menuContent: Navigation | null;
}

const GlobalUiContext = createContext<Values>({
    appClassName: 'expanded',
    setAppClassName: () => null,
    appSidebarHidden: false,
    setAppSidebarHidden: () => null,
    pushToast: () => null,
    pushSuccessToast: () => null,
    pushErrorToast: () => null,
    pushWarningToast: () => null,
    pushInfoToast: () => null,
    dismissToast: () => null,
    menuContent: null,
});

// TODO: We should remove possibility to pass 'summary' property as it is overridden anyway
// TODO: We should standarize calls to: pushErrorToast('Error Message')
export function GlobalUiProvider({ children }: PropsWithChildren): ReactNode {
    const appNameStorage = window.localStorage.getItem('appClassName') as
        | 'collapsed'
        | 'expanded'
        | null;
    const [appClassName, setAppClassName] = useState<'collapsed' | 'expanded'>(
        appNameStorage ? appNameStorage : 'expanded',
    );
    useEffect(() => {
        window.localStorage.setItem('appClassName', appClassName);
    }, [appClassName]);

    const toastRef = useRef<Toast>(null);
    const toastsByIdRef = useRef<Map<string, ToastMessage>>(new Map());
    const [appSidebarHidden, setAppSidebarHidden] = useState(false);

    const { menuContent } = useMenuContext();

    const pushToast = useCallback((toastMessage: ToastMessage) => {
        const id = toastMessage.id;
        if (typeof id === 'string') {
            if (toastsByIdRef.current.has(id)) return;
            toastsByIdRef.current.set(id, toastMessage);
        }
        toastRef.current?.show(toastMessage);
    }, []);

    const pushInfoToast = useCallback(
        (toastMessage: Omit<ToastMessage, 'severity'>) => {
            pushToast({
                summary: 'Info',
                ...toastMessage,
                severity: 'info',
                content: ToastContent,
                life: 4000,
            });
        },
        [pushToast],
    );

    const pushSuccessToast = useCallback(
        (toastMessage: Omit<ToastMessage, 'severity'>) => {
            pushToast({
                summary: 'Success',
                ...toastMessage,
                severity: 'success',
                content: ToastContent,
                life: 4000,
            });
        },
        [pushToast],
    );

    const pushWarningToast = useCallback(
        (toastMessage: Omit<ToastMessage, 'severity'>) => {
            pushToast({
                summary: 'Warning',
                ...toastMessage,
                severity: 'warn',
                content: ToastContent,
                life: 8000,
            });
        },
        [pushToast],
    );

    const pushErrorToast = useCallback(
        (toastMessage: Omit<ToastMessage, 'severity'>) => {
            pushToast({
                summary: 'Error',
                ...toastMessage,
                severity: 'error',
                content: ToastContent,
                sticky: toastMessage.sticky ?? true,
            });
        },
        [pushToast],
    );

    const dismissToast = useCallback((id: string) => {
        const tracked = toastsByIdRef.current.get(id);
        if (!tracked) return;
        toastRef.current?.remove(tracked);
        toastsByIdRef.current.delete(id);
    }, []);

    const handleToastRemove = useCallback((removed: ToastMessage) => {
        if (typeof removed.id === 'string') {
            toastsByIdRef.current.delete(removed.id);
        }
    }, []);

    const globalUiState = useMemo(
        () => ({
            appClassName,
            setAppClassName,
            pushToast,
            pushInfoToast,
            pushSuccessToast,
            pushWarningToast,
            pushErrorToast,
            dismissToast,
            appSidebarHidden,
            setAppSidebarHidden,
            menuContent,
        }),
        [
            appClassName,
            appSidebarHidden,
            menuContent,
            pushToast,
            pushErrorToast,
            pushInfoToast,
            pushSuccessToast,
            pushWarningToast,
            dismissToast,
        ],
    );

    const contentAreaElement = document.getElementById('app-main');
    return (
        <GlobalUiContext.Provider value={globalUiState}>
            <Toast
                ref={toastRef}
                position="top-center"
                appendTo={contentAreaElement}
                baseZIndex={100}
                onRemove={handleToastRemove}
            />
            {children}
        </GlobalUiContext.Provider>
    );
}

export function useGlobalUI(): Values {
    return useContext(GlobalUiContext);
}

export function useToast(): ToastContext {
    const {
        pushToast,
        pushInfoToast,
        pushSuccessToast,
        pushWarningToast,
        pushErrorToast,
        dismissToast,
    } = useContext(GlobalUiContext);

    return {
        pushToast,
        pushInfoToast,
        pushSuccessToast,
        pushWarningToast,
        pushErrorToast,
        dismissToast,
    };
}
